<!--
  Copyright (C) 2025 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div
    class="workspace-card"
    :class="{ 'workspace-card-clickable': clickable }"
  >
    <!-- Color accent bar -->
    <div class="workspace-card-accent"></div>

    <div class="workspace-card-inner">
      <div class="workspace-card-body" @click="handleClick">
        <!-- Content -->
        <div class="workspace-card-content">
          <div class="workspace-card-header">
            <span class="workspace-card-title" v-tooltip.top="workspace.title">
              {{ workspace.title }}
            </span>
            <div class="workspace-card-actions" @click.stop>
              <slot name="actions">
                <button
                  v-if="showActionMenu"
                  type="button"
                  class="workspace-action-btn"
                  v-tooltip.top="actionMenuTooltip"
                  @click="$emit('action-menu', $event, workspace)"
                >
                  <i class="ri-more-2-line"></i>
                </button>
              </slot>
            </div>
          </div>

          <p
            v-if="showDescription"
            v-tooltip.bottom="description || null"
            class="workspace-card-description"
            :class="{ 'no-description': !description }"
          >
            {{ description || noDescriptionText }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="workspace-card-footer">
        <div class="workspace-card-meta">
          <span
            v-if="showRole && workspace.role"
            v-tooltip.top="roleTooltip"
            class="workspace-role-badge"
            :class="[`workspace-role-${workspace.role.toLowerCase()}`]"
          >
            {{ roleLabel }}
          </span>
          <slot name="meta"></slot>
        </div>
        <div class="workspace-card-info">
          <i
            v-if="showVisibility"
            v-tooltip.top="visibilityLabel"
            :class="visibilityIcon"
            class="workspace-visibility-icon"
          />
          <slot name="footer-end"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Tooltip from "primevue/tooltip";

export default {
  name: "WorkspaceCard",
  directives: {
    tooltip: Tooltip,
  },
  props: {
    /**
     * Workspace object with properties: title, description, visibility, role, etc.
     */
    workspace: {
      type: Object,
      required: true,
    },
    /**
     * Show the description
     */
    showDescription: {
      type: Boolean,
      default: true,
    },
    /**
     * Show the visibility icon
     */
    showVisibility: {
      type: Boolean,
      default: true,
    },
    /**
     * Show the role badge
     */
    showRole: {
      type: Boolean,
      default: true,
    },
    /**
     * Show the action menu button (3 dots)
     */
    showActionMenu: {
      type: Boolean,
      default: true,
    },
    /**
     * Make the card clickable
     */
    clickable: {
      type: Boolean,
      default: true,
    },
    /**
     * Tooltip for action menu button
     */
    actionMenuTooltip: {
      type: String,
      default: "Actions",
    },
    /**
     * Text shown when no description
     */
    noDescriptionText: {
      type: String,
      default: "Aucune description",
    },
    /**
     * Custom role labels mapping
     */
    roleLabels: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Custom visibility labels mapping
     */
    visibilityLabels: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Custom role tooltips mapping (explains what the role means)
     */
    roleTooltips: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["click", "action-menu"],
  computed: {
    description() {
      return (
        this.workspace.description ||
        this.workspace.properties?.["cm:description"] ||
        ""
      );
    },
    visibilityIcon() {
      const icons = {
        PUBLIC: "ri-global-line",
        PRIVATE: "ri-lock-line",
        MODERATED: "ri-shield-user-line",
      };
      return icons[this.workspace.visibility] || "ri-question-line";
    },
    visibilityLabel() {
      if (this.visibilityLabels[this.workspace.visibility]) {
        return this.visibilityLabels[this.workspace.visibility];
      }
      const defaultLabels = {
        PUBLIC: "Public",
        PRIVATE: "Privé",
        MODERATED: "Modéré",
      };
      return (
        defaultLabels[this.workspace.visibility] || this.workspace.visibility
      );
    },
    roleLabel() {
      if (this.roleLabels[this.workspace.role]) {
        return this.roleLabels[this.workspace.role];
      }
      return this.workspace.role;
    },
    roleTooltip() {
      if (this.roleTooltips[this.workspace.role]) {
        return this.roleTooltips[this.workspace.role];
      }
      const defaultTooltips = {
        SiteManager: "Vous pouvez gérer cet espace",
        SiteCollaborator: "Vous pouvez modifier le contenu",
        SiteContributor: "Vous pouvez ajouter du contenu",
        SiteConsumer: "Vous pouvez consulter le contenu",
      };
      return defaultTooltips[this.workspace.role] || "";
    },
  },
  methods: {
    handleClick() {
      if (this.clickable) {
        this.$emit("click", this.workspace);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.workspace-card {
  display: flex;
  flex-direction: row;
  border: 1px solid var(--p-surface-border);
  border-radius: 0.5rem;
  background-color: var(--p-surface-card);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  height: 100%;
  min-height: 140px;
  overflow: hidden;

  &.workspace-card-clickable {
    cursor: pointer;

    &:hover {
      border-color: var(--p-primary-color);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

      .workspace-card-accent {
        width: 5px;
      }
    }
  }
}

.workspace-card-accent {
  width: 4px;
  flex-shrink: 0;
  background-color: var(--p-primary-color);
  transition: width 0.2s ease;
}

.workspace-card-inner {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 1rem;
  min-width: 0;
}

.workspace-card-body {
  display: flex;
  flex: 1;
}

.workspace-card-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.workspace-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.workspace-card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--p-text-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

.workspace-card-actions {
  flex-shrink: 0;
}

.workspace-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background-color: transparent;
  color: var(--p-text-muted-color);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: var(--p-surface-hover);
    color: var(--p-text-color);
  }

  &:active {
    background-color: var(--p-content-hover-background);
  }
}

.workspace-card-description {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;

  &.no-description {
    font-style: italic;
    opacity: 0.7;
  }
}

.workspace-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--p-surface-border);
}

.workspace-card-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.workspace-card-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.workspace-visibility-icon {
  font-size: 1rem;
  color: var(--p-text-muted-color);
}

.workspace-role-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  border-radius: 1rem;
}

.workspace-role-sitemanager {
  background-color: var(--p-blue-100);
  color: var(--p-blue-700);
}

.workspace-role-sitecollaborator {
  background-color: var(--p-orange-100);
  color: var(--p-orange-700);
}

.workspace-role-sitecontributor {
  background-color: var(--p-purple-100);
  color: var(--p-purple-700);
}

.workspace-role-siteconsumer {
  background-color: var(--p-green-100);
  color: var(--p-green-700);
}
</style>
